/**
 * Arcade credits for "Dams to AGI" — shared by the server (verified games)
 * and the client (free play), so both follow the same rules.
 *
 * A credit is 3 lives. Lives carry from stage to stage; build stages spend
 * them on hits and floods, defend stages on being drained. Out of lives, the
 * player inserts a coin to continue on the same stage with their energy, or
 * it's game over. Every played stage pays a small WOOD bonus and every clear
 * a bigger one, so the balance grows just for playing.
 */

import { getStageConfig } from "./stageLevels";

export const START_LIVES = 3;
export const CONTINUE_LIVES = 3;
export const PLAY_BONUS_WH = 500; // +50 logs for every stage played
export const CLEAR_BONUS_WH_PER_STAGE = 1000; // +100 logs x stage for a clear
export const FINAL_STAGE = 101;
const WH_PER_LOG = 10;

/** @typedef {{ stage: number, lives: number, bankWh: number, over: boolean, finished: boolean }} Game */

/** @returns {Game} */
export function newGame() {
  return { stage: 1, lives: START_LIVES, bankWh: 0, over: false, finished: false };
}

/** A tester's game started on `stage`, with the energy that stage defaults to. @returns {Game} */
export function gameAtStage(stage) {
  return { ...newGame(), stage, bankWh: getStageConfig(stage).defaultStartBankWh };
}

/** @param {Game} g @returns {Game} */
export function continueGame(g) {
  return { ...g, lives: CONTINUE_LIVES, over: false };
}

/** Quitting mid-stage costs a life, like dying. @param {Game} g @returns {Game} */
export function abandonStage(g) {
  const lives = Math.max(0, g.lives - 1);
  return { ...g, lives, over: lives === 0 };
}

/**
 * Apply a finished stage (StageSim `result()`) to the game.
 * @param {Game} g
 * @param {{ status: string, kind: string, lives: number, totalWh: number }} outcome
 * @returns {{ game: Game, bonusWh: number }}
 */
export function applyOutcome(g, outcome) {
  if (outcome.status === "complete") {
    const bonusWh = PLAY_BONUS_WH + CLEAR_BONUS_WH_PER_STAGE * g.stage;
    const finished = g.stage >= FINAL_STAGE;
    const game = {
      ...g,
      stage: finished ? g.stage : g.stage + 1,
      lives: outcome.kind === "build" ? outcome.lives : g.lives,
      bankWh: Math.max(0, outcome.totalWh) + bonusWh,
      finished,
    };
    return { game, bonusWh };
  }
  // Failed: the stage replays from the energy it started with (plus the play bonus)
  const lives = Math.max(0, livesAfterFailure(g, outcome));
  return { game: { ...g, lives, over: lives === 0, bankWh: g.bankWh + PLAY_BONUS_WH }, bonusWh: PLAY_BONUS_WH };
}

/** A build stage already spent its lives on hits; a flood or a drain costs one more. */
function livesAfterFailure(g, outcome) {
  if (outcome.kind !== "build") return g.lives - 1;
  return outcome.lives <= 0 ? 0 : outcome.lives - 1;
}

export function woodLogs(bankWh) {
  return Math.floor(Math.max(0, bankWh) / WH_PER_LOG);
}
